import React, { useState, useEffect, useMemo } from "react";
import useFilteredOrders from "../../hooks/useFilteredOrders";
import ExchangeItemSelect from "../ReturnsView/Exchanges/ExchangeItemSelect";
import { eliminateRemovedItems } from "../utils/common";
import styles from "./PortalOrderFetcher.module.css";

const PortalOrderFetcher = ({ orderId, uniqueOrderId, linkType, businessName, fontFamily }) => {
	const [customerData, setCustomerData] = useState("");
	const [selectedExchangeProducts, setSelectedExchangeProducts] = useState(null);
	const [productsToExclude, setProductsToExclude] = useState(null);
	const [flattenedProducts, setFlattenedProducts] = useState([]);
	const [exchangeProductData, setExchangeProductData] = useState(null);
	const [renderReturnLinkCreator, setRenderReturnLinkCreator] = useState(false);
	const [visibleChoices, setVisibleChoices] = useState([]);
	const [renderExchangeItemSelect, setRenderExchangeItemSelect] = useState(true);
	const [newItemsSelectedOptions, setNewItemsSelectedOptions] = useState([]); //just used as a placeholder to satisfy ExchangeItemSelect
	const [showNoItemsAvailableForReturn, setShowNoItemsAvailableForReturn] = useState(false);
	const { orders, loading, error } = useFilteredOrders(orderId, false);
	const [showLoader, setShowLoader] = useState(true);
	const apiUrl = process.env.REACT_APP_API_URL;
	const businessSlug = businessName.toLowerCase();
	//const allowedTitles = useMemo(() => { return (exchangeProductData || []).map((p) => p.product_title); }, [exchangeProductData]); // Compute allowed titles once per exchangeProductData change
	const allowedSkus = useMemo(() => { return new Set((exchangeProductData || []).flatMap(p => p.shopify_variants.map(v => v.sku))); }, [exchangeProductData]);

	const skuGroups = {"fulton": [["Ath", "Ins", "Uni"], ["HS", "SL"]], "terlingua threads": []} //If "Ath" is selected for example, send up all skus in the group that belongs to because they are all eligible for an exchange

	//fetch all products from the provided orderId that are part of return/exchange orders that have purchased labels
	useEffect(() => {
		const fetchReturnedProducts = async () => {
			try {
				const response = await fetch(`${apiUrl}/api/returned_products/?order_id=${encodeURIComponent(orderId)}&business_slug=${encodeURIComponent(businessSlug)}`, {
					method: "GET",
					headers: {"Content-Type": "application/json"},
				});
				const data = await response.json();
				setProductsToExclude(data.returned_products || []); //products already returned
			} catch (error) { console.error("error fetching returned products:", error); }
		};
		fetchReturnedProducts();
	}, []);

	//immediately create link once a portal option is chosen
	useEffect(() => {
		if (!selectedExchangeProducts || !exchangeProductData || !productsToExclude) { return; }
		//final check to remove any items from selectedExchangeProducts that have skus that aren't in the available array of exchange products (exchangeProductData)
		const validProductSkus = new Set(exchangeProductData.flatMap(p => p.shopify_variants.map(v => String(v.sku || "").trim())).filter(Boolean));
	    var selectedExchangeProductsFinal = selectedExchangeProducts.filter((item) => {
	        const sku = String(item.product_sku || "").trim();
	        return validProductSkus.has(sku);
	    });
		if (selectedExchangeProductsFinal.length === 0) { 
			setShowNoItemsAvailableForReturn(true); 
			return;
		}
		if (linkType === "exchange") { 
			createLink(orderId, selectedExchangeProductsFinal, businessName, customerData, linkType, uniqueOrderId); 
		}
		else { setRenderReturnLinkCreator(true); }
	}, [selectedExchangeProducts, orderId, uniqueOrderId, linkType, businessSlug, customerData, exchangeProductData, productsToExclude]);

	// get items from the order
	useEffect(() => {
		if (loading || !orders?.length || !productsToExclude) { return; }
		const fetchOrderData = async () => {
			try {
				const selectedOrder = orders.find((order) => order.node.name.slice(1) === orderId);
				if (!selectedOrder) { return; }
				const customer = selectedOrder.node.customer;
				const customerObject = {
					firstName: customer.firstName,
					lastName: customer.lastName,
					email: customer.email,
					address: customer.defaultAddress,
				};
				setCustomerData(customerObject);
				const lineItems = selectedOrder.node.lineItems.edges.map((edge) => ({
					product_image: edge.node.product.images.edges[0]?.node.src,
					product_title: edge.node.title,
					product_id: edge.node.product.id,
					variant_id: edge.node.variant.id,
					variant_title: edge.node.variant.title,
					product_cost: edge.node.variant.price * edge.node.quantity,
					product_quantity: edge.node.quantity,
					max_product_quantity: edge.node.quantity,
					product_sku: edge.node.variant.sku,
				}));
				const removedItems = await fetchOrderEdits(selectedOrder.node.id.split("/").pop(), apiUrl);
				let cleanedLineItems;
				if (removedItems) { cleanedLineItems = eliminateRemovedItems([...removedItems], [...lineItems]); }	
				else { cleanedLineItems = lineItems; }
				if (Array.isArray(productsToExclude) && productsToExclude.length) {
					let excludeList = productsToExclude.map((s) => String(s || "").trim()).filter(Boolean);
					cleanedLineItems = cleanedLineItems.reduce((acc, item) => {
						const sku = String(item.product_sku || "").trim();
						const qty = Number(item.product_quantity || 0);
						if (!sku || qty <= 0) {
							acc.push(item);
							return acc;
						}
						let removeCount = 0;
						for (let i = excludeList.length - 1; i >= 0 && removeCount < qty; i--) {
							if (excludeList[i] === sku) {
								excludeList.splice(i, 1);
								removeCount += 1;
							}
						}
						const newQty = qty - removeCount;
						if (newQty > 0) { acc.push({...item, product_quantity: newQty}); }
						return acc;
					}, []);
				}
				setSelectedExchangeProducts(cleanedLineItems);
			} catch (err) { alert("error accessing order."); }
		};
		fetchOrderData();
	}, [loading, orders, orderId, linkType, productsToExclude]);

	//fetch configured exchange products selected by brand from its list of active products on shopify
	useEffect(() => {
		const fetchEligibleExchangeProductData = async () => {
			if (!selectedExchangeProducts) { return; }
			const prices = selectedExchangeProducts.map(item => parseFloat(item.product_cost) / (item.product_quantity || 1));
			let finalSkuList = [];
			if (businessSlug !== "terlingua threads") { //HARDCODE
				// get prefixes like ["Ath","HS"]
				const selectedSkus = selectedExchangeProducts.map(item => item.product_sku.split(/[\/-]/)[0]);
				for (const sku of selectedSkus) {
					if (finalSkuList.includes(sku)) continue;
				  	for (const group of skuGroups[businessName]) {
				    	if (group.includes(sku)) {
					      	for (const gSku of group) {
					        	if (!finalSkuList.includes(gSku)) { finalSkuList.push(gSku); }
					      	}
					      	break; // stop once the matching group is found
				    	}
				  	}
				}
			}
			try {
				const response = await fetch(`${process.env.REACT_APP_API_URL}/api/fetch_eligible_exchange_product_data/?businessName=${encodeURIComponent(businessName)}`, {
					method: "POST",
					headers: {"Content-Type": "application/json"},
					body: JSON.stringify({ "skus": finalSkuList, "prices": prices })
				});
				const data = await response.json();
				setExchangeProductData(data.eligible_exchange_product_data || []);
			} catch (error) { console.error("Failed to fetch exchange products:", error); }
		};
		fetchEligibleExchangeProductData();
	}, [selectedExchangeProducts]);

	// Expand + filter when inputs change
	useEffect(() => {
		if (!selectedExchangeProducts?.length || !exchangeProductData?.length) return;
		let uniqueProductId = 1;
		const expanded = selectedExchangeProducts.flatMap((product) => {
			const quantity = parseInt(product.product_quantity || 1, 10);
			const unitCost = Number(product.product_cost || 0) / (quantity || 1);
			return Array.from({ length: quantity }, () => ({
				...product,
				product_variant: product.variant_title,  //re organize for exchangeItemSelect
				product_cost: Number(unitCost.toFixed(2)),
				product_quantity: 1,
				selected: selectedExchangeProducts.length > 1 || quantity > 1 ? false : true,	
				choices: {},
				written_feedback: "",
				unique_id: uniqueProductId++,
			}));
		});
		const filteredExpanded = expanded.filter(p => allowedSkus.has(p.product_sku));
		// If the order has multiple items but only one is eligible for return/exchange, that item inherits selected:false from the multi-item path. 
		// Auto-select it here so the link isn't created with 0 items when the item selection UI is skipped.
		const autoSelected = filteredExpanded.length === 1 ? filteredExpanded.map(p => ({ ...p, selected: true })) : filteredExpanded;
		setFlattenedProducts(autoSelected);
		if (filteredExpanded.length === 1) { setRenderExchangeItemSelect(false); }
	}, [selectedExchangeProducts, exchangeProductData, allowedSkus]);

	useEffect(() => {
		//create and navigate to return link
		if (renderReturnLinkCreator && !renderExchangeItemSelect) {
			setShowLoader(true);
			const selectedFlattenedProducts = flattenedProducts.filter(p => p.selected);
			createLink(orderId, selectedFlattenedProducts, businessName, customerData, linkType, uniqueOrderId)
		}
	}, [renderReturnLinkCreator, renderExchangeItemSelect])

	//create link and take user to it
	const createLink = async (orderId, selectedItems, businessName, customerData, linkType, uniqueOrderId) => {
		try {
		  const response = await fetch(`${apiUrl}/api/create_return_link/`, {
		    method: "POST",
		    headers: {"Content-Type": "application/json"},
		    body: JSON.stringify({order_id: orderId, selected_items: selectedItems, business_name: businessName, customer_data: customerData, link_type: linkType, unique_id: uniqueOrderId}),
		  });
		  const data = await response.json();
		  if (data.link === "no items") { alert("no items available from this order!"); }
		  else { window.location.href = data.link; }
		} catch (orderLookUpError) { alert(`error creating ${linkType} link: ${orderLookUpError}`); }
	};

	// request refund data
	const fetchOrderEdits = async (selectedOrderShopifyId, apiUrl) => {
		const response = await fetch(
			`${apiUrl}/api/get_order_edits/?unique_order_id=${encodeURIComponent(selectedOrderShopifyId)}&business=${encodeURIComponent(businessSlug)}`, {
				method: "GET",
				headers: {"Content-Type": "application/json"},
			}
		);
		const data = await response.json();
		if (data.data.refunds.length === 0) return null;
		return data.data.refunds[0].refund_line_items.map((item) => {
			const cleanedName = item.line_item.name.replace(/[\/\-]/g, " ").replace(/\s+/g, " ").toLowerCase().trim();
			return {name: cleanedName, quantity: item.quantity, sku: item.line_item.sku};
		});
	};
	return (
		<div className={`${styles.portal_loader_container} ${fontFamily}`}>
			{showNoItemsAvailableForReturn && (<p className={`${styles.no_items_available} ${fontFamily}`}>No items available for return.</p>)}
			{((!renderReturnLinkCreator && !showNoItemsAvailableForReturn) || (renderReturnLinkCreator && !renderExchangeItemSelect)) && (<img className={styles.portal_loader} src={`${process.env.PUBLIC_URL}/images/shipwheel_loader.gif`} alt="portal_loader"/>)}
			{renderReturnLinkCreator && renderExchangeItemSelect && (<ExchangeItemSelect selectedExchangeProducts={selectedExchangeProducts} visibleChoices={visibleChoices} setVisibleChoices={setVisibleChoices} setRenderExchangeItemSelect={setRenderExchangeItemSelect} setRenderItemExchange={null} flattenedProducts={flattenedProducts} setFlattenedProducts={setFlattenedProducts} linkType={"return"} setNewItemsSelectedOptions={setNewItemsSelectedOptions} />)}
		</div>
	);
};

export default PortalOrderFetcher;